/* Grid plate laser-position converter. Port of IntelAlign's GridPlate.swift.
   A grid plate is graduated for one reference wheelbase and wheel diameter; on any
   other car its printed numbers no longer read toe correctly. Equating the throw
   at the plate, computed over the car and read off the plate's own scale:
       W_car x tan(toe_car) = W_plate x tan(toe_plate)
       laser position = toe x (W_car / W_plate) x (D_plate / D_car)
   Everything is per wheel. Ratios scale a distance, not an angle, so the toe is
   resolved to the chord at the car's wheel, scaled, then read back as an angle
   across the plate's reference wheel. */
(function (root) {
  'use strict';

  var MM_PER_INCH = 25.4;
  var LENGTH_PER_MM = { mm: 1, inch: MM_PER_INCH };
  var TOE = {
    mm:     { symbol: 'mm', longName: 'millimeters', digits: 2, angular: false },
    inch:   { symbol: 'in', longName: 'inches',      digits: 2, angular: false },
    arcmin: { symbol: '′', longName: 'minutes',  digits: 1, angular: true  },
    degree: { symbol: '°', longName: 'degrees', digits: 3, angular: true  }
  };
  var UNIVERSAL = { wheelbaseMM: 2540, graduationUnit: 'arcmin' };
  /* The same plausibility limits as the app (DigitalToeLive.swift): a wheelbase in
     inches typed into a millimeter field, or the reverse, is caught by the ranges
     not overlapping, and no genuine value is ever refused. */
  var LIMITS = { wheelbaseMM: [500, 6000], wheelIN: [4, 36] };
  var PROBLEMS = {
    plateWheelbaseMissing: 'Enter the wheelbase the grid plate is graduated for.',
    carWheelbaseMissing: "Enter the vehicle's wheelbase.",
    wheelDiameterMissing: 'Enter the wheel diameter. Toe is an angle, and a figure in millimeters or inches only means something across a stated wheel.',
    plateWheelbaseImplausible: 'That plate wheelbase is outside 500 to 6000 mm (20 to 236 in).',
    carWheelbaseImplausible: 'That wheelbase is outside 500 to 6000 mm (20 to 236 in).',
    wheelDiameterImplausible: 'Wheel diameters are in inches and should be between 4 and 36.',
    toeNegative: 'Enter toe as a positive figure and pick In or Out.'
  };
  /* The likely slip behind an implausible wheelbase, by the unit the field is in. */
  var HINTS = { mm: ' Inches typed into a millimeter field?', inch: ' Millimeters typed into an inch field?' };
  /* The engraved plate, as laid out in the Grid Plate Generator's LightBurn master
     (BMW_-_E92_-_mm.lbrn2), turned on its side the way the plate is bolted down.
     Geometry is the master's three symmetric plates (the fourth sits 0.78 mm off).
     Every line sits at its true offset for the plate's own wheelbase (and wheel, on a
     millimeter plate): four lines to a labelled group, the half line longer than the
     quarters, and anything past 1.35" (34.29 mm) from zero left off, exactly as the
     generator trims it. Lengths and positions are the master's, in millimeters; the
     drawing then spreads the engraved range across the plate's width for legibility. */
  var PLATE = {
    cutoffMM: 34.29,
    windowMM: 38.5,
    edgeMM: 4.2,
    halfHeightMM: 28.3,
    tagMM: 8,
    zero:    [-26.67, 26.67],
    major:   [-15.875, 15.875],
    half:    [-13.97, 13.97],
    quarter: [-12.84, 12.84],
    labelFromMM: 16.65, labelSizeMM: 2.8,
    toeTextMM: -19.56, toeTextSizeMM: 3.03, toeTextStartMM: 2.6,
    groups: {
      mm:     { major: 1,    count: 5, label: function (v) { return v + 'mm'; } },
      arcmin: { major: 10,   count: 4, label: function (v) { return v + "'"; } },
      degree: { major: 0.2,  count: 4, label: function (v) { return v.toFixed(1) + '°'; } },
      inch:   { major: 0.05, count: 4, label: function (v) { return v.toFixed(2) + 'in'; } }
    }
  };

  /* Where a toe figure lands across the plate, in millimeters from the zero line:
     the plate's wheelbase times the tangent of the angle its graduation stands for. */
  function throwMM(v, unit, wheelbaseMM, wheelIN) {
    var d = degreesFromToe(v, unit, wheelIN);
    return d === null ? 0 : wheelbaseMM * Math.tan(d * Math.PI / 180);
  }

  function toMM(value, unit) { return value * LENGTH_PER_MM[unit]; }

  function degreesFromToe(value, unit, wheelIN) {
    if (unit === 'degree') return value;
    if (unit === 'arcmin') return value / 60;
    if (!(wheelIN > 0)) return null;
    var chordMM = unit === 'mm' ? value : value * MM_PER_INCH;
    return Math.atan(chordMM / (wheelIN * MM_PER_INCH)) * 180 / Math.PI;
  }

  function toeFromDegrees(deg, unit, wheelIN) {
    if (unit === 'degree') return deg;
    if (unit === 'arcmin') return deg * 60;
    if (!(wheelIN > 0)) return null;
    var chordMM = wheelIN * MM_PER_INCH * Math.tan(deg * Math.PI / 180);
    return unit === 'mm' ? chordMM : chordMM / MM_PER_INCH;
  }

  function convert(o) {
    var plateMM = toMM(o.plateWheelbase, o.plateWheelbaseUnit);
    var carMM = toMM(o.carWheelbase, o.carWheelbaseUnit);
    if (!(plateMM > 0)) return { ok: false, problem: 'plateWheelbaseMissing' };
    if (!(carMM > 0)) return { ok: false, problem: 'carWheelbaseMissing' };
    if (!TOE[o.toeUnit].angular && !(o.carWheelDiameterIN > 0)) return { ok: false, problem: 'wheelDiameterMissing' };
    if (!TOE[o.plateGraduationUnit].angular && !(o.plateWheelDiameterIN > 0)) return { ok: false, problem: 'wheelDiameterMissing' };
    var carDia = o.carWheelDiameterIN > 0 ? o.carWheelDiameterIN : 1;
    var plateDia = o.plateWheelDiameterIN > 0 ? o.plateWheelDiameterIN : 1;
    var wheelbaseRatio = carMM / plateMM;
    var diameterRatio = plateDia / carDia;
    var ratio = wheelbaseRatio * diameterRatio;
    var toeDeg = degreesFromToe(o.toe, o.toeUnit, carDia);
    if (toeDeg === null) return { ok: false, problem: 'wheelDiameterMissing' };
    var carChordMM = carDia * MM_PER_INCH * Math.tan(toeDeg * Math.PI / 180);
    var plateChordMM = carChordMM * ratio;
    var plateDeg = Math.atan(plateChordMM / (plateDia * MM_PER_INCH)) * 180 / Math.PI;
    var position = toeFromDegrees(plateDeg, o.plateGraduationUnit, plateDia);
    if (position === null) return { ok: false, problem: 'wheelDiameterMissing' };
    var matchesTarget = Math.abs(position - o.toe) <= 0.0005 * Math.max(Math.abs(o.toe), 1);
    return { ok: true, result: {
      laserPosition: position, toeDegrees: toeDeg, plateDegrees: plateDeg,
      wheelbaseRatio: wheelbaseRatio, diameterRatio: diameterRatio, ratio: ratio,
      toeTarget: o.toe, toeUnit: o.toeUnit, plateGraduationUnit: o.plateGraduationUnit,
      matchesTarget: matchesTarget,
      isDirectReading: o.toeUnit === o.plateGraduationUnit && matchesTarget,
      isSameWheelbase: Math.abs(wheelbaseRatio - 1) < 0.0005
    } };
  }

  var api = { MM_PER_INCH: MM_PER_INCH, TOE: TOE, UNIVERSAL: UNIVERSAL, PROBLEMS: PROBLEMS, HINTS: HINTS, PLATE: PLATE, LIMITS: LIMITS, throwMM: throwMM,
              degreesFromToe: degreesFromToe, toeFromDegrees: toeFromDegrees, convert: convert };
  root.AocGridPlate = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;

  /* ───────────────────────── page wiring ───────────────────────── */
  if (typeof document === 'undefined') return;

  var STORE_KEY = 'aoc-grid-plate';
  /* Inside Shopify's theme editor the section must show its configured defaults, not
     whatever the merchant's own browser remembered from a previous try. */
  var designMode = function () { return !!(root.Shopify && root.Shopify.designMode); };
  function fmt(v, unit) { return v.toFixed(TOE[unit].digits); }
  function withUnit(v, unit) { var s = fmt(v, unit); return TOE[unit].angular ? s + TOE[unit].symbol : s + ' ' + TOE[unit].symbol; }
  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }

  /* One corner's plate: black hardcoat anodize, white engraving, the laser line in
     green. `o` carries pos (laser position in plate units), unit, flipped (right-hand
     wheels read their plate mirrored, toe-in on the left), wheelbaseMM and wheelIN (the
     plate's own spec), wheelName ("LEFT FRONT") and id (unique per plate on the page).
     Drawn in millimeters; y runs down, so a length along the line is negated.
     Returns the markup and whether the line sits past the engraved scale. */
  function plateSVG(o) {
    var P = PLATE, g = P.groups[o.unit] || P.groups.mm, W = P.windowMM, H = P.halfHeightMM;
    var at = function (v) { return throwMM(v, o.unit, o.wheelbaseMM, o.wheelIN); };
    var u = function (mm) { return o.flipped ? -mm : mm; };
    var n = function (v) { return String(Math.round(v * 1000) / 1000); };
    var gid = 'gp-anod-' + o.id, out = [];
    out.push('<defs><linearGradient id="' + gid + '" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#2c2c2e"/><stop offset="1" stop-color="#151516"/></linearGradient></defs>');
    out.push('<rect x="' + (-W) + '" y="' + (-H) + '" width="' + (2 * W) + '" height="' + (2 * H) + '" rx="2.2" fill="url(#' + gid + ')" class="gp-plate__body"/>');
    var line = function (x, ext, cls) {
      return '<line x1="' + n(x) + '" y1="' + n(-ext[1]) + '" x2="' + n(x) + '" y2="' + n(-ext[0]) + '" class="gp-plate__line ' + cls + '" vector-effect="non-scaling-stroke"/>';
    };
    var steps = g.count * 4, step = g.major / 4, maxOff = 0, text = [], ticks = [];
    for (var j = -steps; j <= steps; j++) {
      var o2 = at(j * step);
      if (Math.abs(o2) > P.cutoffMM + 1e-6) continue;
      maxOff = Math.max(maxOff, Math.abs(o2)); ticks.push([j, o2]);
    }
    /* Spread the engraved range across the plate's width so the lines read clearly at
       card size. Spacing stays even and proportional; only the drawing scale changes. */
    var spread = maxOff > 0 ? (W - P.edgeMM) / maxOff : 1;
    for (var q = 0; q < ticks.length; q++) {
      var i = ticks[q][0], off = ticks[q][1] * spread;
      var k = Math.abs(i), x = u(off);
      if (i === 0) { out.push(line(x, P.zero, 'is-zero')); continue; }
      if (k % 4 === 0) {
        out.push(line(x, P.major, 'is-major'));
        text.push('<text class="gp-plate__num" transform="translate(' + n(x) + ' ' + n(-P.labelFromMM) + ') rotate(-90)" font-size="' + P.labelSizeMM + '" dominant-baseline="central">' +
                 esc(g.label(parseFloat((k / 4 * g.major).toFixed(3)))) + '</text>');
      } else {
        out.push(k % 2 === 0 ? line(x, P.half, 'is-half') : line(x, P.quarter, 'is-quarter'));
      }
    }
    /* TOE IN and TOE OUT under their halves, naming the wheel this plate reads. */
    var inSide = o.flipped ? -1 : 1;
    [['IN', inSide], ['OUT', -inSide]].forEach(function (s) {
      text.push('<text class="gp-plate__toe" x="' + n(s[1] * P.toeTextStartMM) + '" y="' + n(-P.toeTextMM) + '" font-size="' + P.toeTextSizeMM + '" text-anchor="' + (s[1] > 0 ? 'start' : 'end') + '" dominant-baseline="central">' +
               esc(o.wheelName + ' TOE ' + s[0]) + '</text>');
    });
    /* The laser line, clamped to the plate, orange once it is past the engraved scale. */
    var lo = at(o.pos) * spread, isOff = Math.abs(lo) > maxOff * spread + 0.3;
    var lx = u(Math.max(-(W - 0.8), Math.min(W - 0.8, lo))), mod = isOff ? ' is-off' : '';
    out.push('<line x1="' + n(lx) + '" y1="' + n(-H) + '" x2="' + n(lx) + '" y2="' + n(H) + '" class="gp-plate__glow' + mod + '"/>');
    out.push('<line x1="' + n(lx) + '" y1="' + n(-H) + '" x2="' + n(lx) + '" y2="' + n(H) + '" class="gp-plate__laser' + mod + '"/>');
    out.push(text.join(''));
    /* The value in a tag above the plate, pointing at the line. */
    var label = withUnit(o.pos, o.unit) + (isOff ? ', off scale' : ''), fs = 3.2;
    var tw = label.length * fs * 0.56 + 3.6, th = 5.4, ty = -H - P.tagMM + 0.4;
    var tx = Math.max(-W + tw / 2, Math.min(W - tw / 2, lx));
    out.push('<rect x="' + n(tx - tw / 2) + '" y="' + n(ty) + '" width="' + n(tw) + '" height="' + th + '" rx="' + (th / 2) + '" class="gp-plate__tag' + mod + '"/>');
    out.push('<path d="M' + n(lx - 1.3) + ' ' + n(ty + th - 0.2) + 'L' + n(lx + 1.3) + ' ' + n(ty + th - 0.2) + 'L' + n(lx) + ' ' + n(-H + 0.1) + 'Z" class="gp-plate__tag' + mod + '"/>');
    out.push('<text x="' + n(tx) + '" y="' + n(ty + th / 2) + '" font-size="' + fs + '" text-anchor="middle" dominant-baseline="central" class="gp-plate__tagtext">' + esc(label) + '</text>');
    var vbTop = -H - P.tagMM;
    return { off: isOff, svg: '<svg class="gp-svg gp-plate" viewBox="' + (-W) + ' ' + vbTop + ' ' + (2 * W) + ' ' + (2 * H + P.tagMM) + '" role="img" aria-label="' +
           esc(o.wheelName.toLowerCase() + ' toe plate, laser line at ' + label) + '">' + out.join('') + '</svg>' };
  }

  function init(rootEl) {
    if (!rootEl || rootEl.dataset.gpReady) return;
    rootEl.dataset.gpReady = '1';
    var q = function (sel) { return rootEl.querySelector(sel); };
    var f = {
      plateWheelbase: q('[name="plate_wheelbase"]'), plateWheel: q('[name="plate_wheel"]'),
      carWheelbase: q('[name="car_wheelbase"]'), frontWheel: q('[name="front_wheel"]'), rearWheel: q('[name="rear_wheel"]'),
      frontToe: q('[name="front_toe"]'), rearToe: q('[name="rear_toe"]')
    };
    var radio = function (name) { var r = rootEl.querySelector('input[name="' + name + '"]:checked'); return r ? r.value : null; };
    var setRadio = function (name, v) { var r = rootEl.querySelector('input[name="' + name + '"][value="' + v + '"]'); if (r) r.checked = true; };
    var num = function (el) { var v = el ? parseFloat(el.value) : NaN; return isFinite(v) ? v : null; };
    var out = q('[data-gp-out]'), calc = q('[data-gp-calc]'), plateNote = q('[data-gp-plate-note]'), toeNote = q('[data-gp-toe-note]');
    var viewNote = q('[data-gp-view-note]'), status = q('[data-gp-status]'), panel = q('.gp__results');
    /* Headings inside the results follow the page's own heading level (set by the section). */
    var subTag = /^h[2-6]$/.test(rootEl.dataset.gpSubtag || '') ? rootEl.dataset.gpSubtag : 'h4';
    /* Only write to the page when the content changed: repainting the same markup on
       every keystroke makes screen readers re-announce it and wastes layout work. */
    var last = {}, touched = false;
    var put = function (el, key, s, asText) {
      if (!el || last[key] === s) return;
      last[key] = s;
      if (asText) el.textContent = s; else el.innerHTML = s;
    };

    function state() {
      return {
        plateWheelbase: num(f.plateWheelbase) || 0, plateUnit: radio('plate_unit') || 'mm', plateReads: radio('plate_reads') || 'arcmin', plateWheel: num(f.plateWheel) || 0,
        carWheelbase: num(f.carWheelbase) || 0, carUnit: radio('car_unit') || 'mm', toeUnit: radio('toe_unit') || 'mm',
        frontWheel: num(f.frontWheel) || 0, rearWheel: num(f.rearWheel) || 0,
        frontToe: num(f.frontToe), rearToe: num(f.rearToe), frontIn: radio('front_dir') !== 'out', rearIn: radio('rear_dir') !== 'out'
      };
    }

    /* The plate specs and the view are remembered once the visitor has changed
       something, so a return visit starts on their plate. Never in the theme editor. */
    function remember(st) {
      if (!touched || designMode() || rootEl.dataset.remember !== 'true') return;
      try { localStorage.setItem(STORE_KEY, JSON.stringify({ wheelbase: st.plateWheelbase, unit: st.plateUnit, reads: st.plateReads, wheel: st.plateWheel, view: radio('view') })); } catch (e) {}
    }
    function recall() {
      if (designMode() || rootEl.dataset.remember !== 'true') return;
      try {
        var s = JSON.parse(localStorage.getItem(STORE_KEY) || 'null'); if (!s) return;
        if (s.wheelbase > 0) f.plateWheelbase.value = s.wheelbase;
        if (s.unit === 'mm' || s.unit === 'inch') setRadio('plate_unit', s.unit);
        if (s.reads && TOE[s.reads]) setRadio('plate_reads', s.reads);
        if (s.wheel > 0) f.plateWheel.value = s.wheel;
        if (s.view === 'standard' || s.view === 'car') setRadio('view', s.view);
      } catch (e) {}
    }

    function axle(st, front) {
      var mag = front ? st.frontToe : st.rearToe;
      if (mag === null) return null;
      var signed = mag * ((front ? st.frontIn : st.rearIn) ? 1 : -1);
      return convert({ toe: signed, toeUnit: st.toeUnit, plateWheelbase: st.plateWheelbase, plateWheelbaseUnit: st.plateUnit,
                       carWheelbase: st.carWheelbase, carWheelbaseUnit: st.carUnit, plateGraduationUnit: st.plateReads,
                       plateWheelDiameterIN: st.plateWheel, carWheelDiameterIN: front ? st.frontWheel : st.rearWheel });
    }

    /* One corner's card, labelled as the app labels it: the wheel's toe, and the grid
       plate it is read on (same side, opposite end). */
    function cornerCell(code, name, plateName, r, plate, flipped) {
      var p = plateSVG({ pos: r.laserPosition, unit: plate.unit, flipped: flipped, wheelbaseMM: plate.wbMM, wheelIN: plate.wheelIN, wheelName: name.toUpperCase(), id: code });
      return '<div class="gp-corner"><p class="gp-corner__label"><span class="gp-corner__badge">' + code + '</span>' + name + ' Toe <span class="gp-corner__on">Shown on ' + plateName + ' Grid Plate</span></p>' +
             '<p class="gp-corner__value' + (p.off ? ' is-off' : '') + '">' + esc(withUnit(r.laserPosition, plate.unit)) +
             (p.off ? '<span class="gp-corner__flag">Past the plate’s engraved scale</span>' : '') + '</p>' + p.svg + '</div>';
    }
    function problemRow(text, info) { return '<div class="gp-problem' + (info ? ' gp-problem--info' : '') + '">' + esc(text) + '</div>'; }
    /* Everything the results panel shows, written once per change, plus a one-line
       spoken summary for the live region. */
    function show(outHtml, calcHtml, spoken) {
      put(out, 'out', outHtml); put(calc, 'calc', calcHtml); put(status, 'status', spoken, true);
      stickyTop();
    }

    function render() {
      var st = state();
      var linearPlate = !TOE[st.plateReads].angular, linearToe = !TOE[st.toeUnit].angular;
      rootEl.querySelectorAll('[data-gp-show="plate_linear"]').forEach(function (el) { el.hidden = !linearPlate; });
      rootEl.querySelectorAll('[data-gp-show="toe_linear"]').forEach(function (el) { el.hidden = !linearToe; });
      rootEl.querySelectorAll('[data-gp-toe-symbol]').forEach(function (el) { el.textContent = TOE[st.toeUnit].symbol; });
      var carView = radio('view') === 'car';
      /* The same two descriptions as the app's view picker. */
      put(viewNote, 'view', carView
        ? 'Each plate as you face it during alignment: the front looking back, the rear looking forward.'
        : "God's eye view from above: front of the car at the top, its left on the left.", true);
      put(plateNote, 'plateNote', linearPlate
        ? "The wheelbase and wheel diameter the plate's markings were laid out for, and the units printed on it."
        : "The wheelbase the plate's markings were laid out for, and the units printed on it. A plate reading in " + TOE[st.plateReads].longName + ' states an angle, so no wheel diameter is needed.', true);
      put(toeNote, 'toeNote', 'In ' + TOE[st.toeUnit].longName + ', per wheel. Front applies to both front wheels, rear to both rears. Pick In or Out; leave an axle blank to skip it.', true);
      remember(st);

      /* Field ranges follow the unit in play, so the browser nudges before the math refuses. */
      var wbMin = LIMITS.wheelbaseMM[0], wbMax = LIMITS.wheelbaseMM[1];
      f.plateWheelbase.min = st.plateUnit === 'mm' ? wbMin : Math.floor(wbMin / MM_PER_INCH); f.plateWheelbase.max = st.plateUnit === 'mm' ? wbMax : Math.ceil(wbMax / MM_PER_INCH);
      f.carWheelbase.min = st.carUnit === 'mm' ? wbMin : Math.floor(wbMin / MM_PER_INCH); f.carWheelbase.max = st.carUnit === 'mm' ? wbMax : Math.ceil(wbMax / MM_PER_INCH);
      [f.plateWheel, f.frontWheel, f.rearWheel].forEach(function (el) { el.min = LIMITS.wheelIN[0]; el.max = LIMITS.wheelIN[1]; });
      var inRange = function (v, r) { return v >= r[0] && v <= r[1]; };

      var plateMM = toMM(st.plateWheelbase, st.plateUnit), carMM = toMM(st.carWheelbase, st.carUnit);
      var wbProblem = !(plateMM > 0) ? 'plateWheelbaseMissing' : !inRange(plateMM, LIMITS.wheelbaseMM) ? 'plateWheelbaseImplausible'
                    : !(carMM > 0) ? 'carWheelbaseMissing' : !inRange(carMM, LIMITS.wheelbaseMM) ? 'carWheelbaseImplausible'
                    : (linearPlate && st.plateWheel > 0 && !inRange(st.plateWheel, LIMITS.wheelIN)) ? 'wheelDiameterImplausible' : null;
      if (wbProblem) {
        var msg = PROBLEMS[wbProblem] + (wbProblem === 'plateWheelbaseImplausible' ? HINTS[st.plateUnit] : wbProblem === 'carWheelbaseImplausible' ? HINTS[st.carUnit] : '');
        return show(problemRow(msg), '', msg);
      }
      if (st.frontToe === null && st.rearToe === null) {
        var prompt = 'Enter the desired toe for an axle to see its laser line.';
        return show(problemRow(prompt, true), '', prompt);
      }

      var rows = [], spoken = [], results = { front: null, rear: null };
      var plate = { unit: st.plateReads, wbMM: plateMM, wheelIN: st.plateWheel };
      [['front', 'LF', 'Left Front', 'RF', 'Right Front', 'Left Rear', 'Right Rear', st.frontWheel], ['rear', 'LR', 'Left Rear', 'RR', 'Right Rear', 'Left Front', 'Right Front', st.rearWheel]].forEach(function (a) {
        var front = a[0] === 'front', raw = front ? st.frontToe : st.rearToe;
        if (raw === null) return;
        if (raw < 0) { rows.push(problemRow(PROBLEMS.toeNegative)); spoken.push(PROBLEMS.toeNegative); return; }
        if (linearToe && a[7] > 0 && !inRange(a[7], LIMITS.wheelIN)) { rows.push(problemRow(PROBLEMS.wheelDiameterImplausible)); spoken.push(PROBLEMS.wheelDiameterImplausible); return; }
        var res = axle(st, front); if (!res) return;
        if (!res.ok) { rows.push(problemRow(PROBLEMS[res.problem])); spoken.push(PROBLEMS[res.problem]); return; }
        results[a[0]] = res.result;
        /* Right-hand wheels read their plate mirrored (toe-in on the left) in BOTH views;
           the views differ only in layout. Standard is a God's eye view: from above, front
           of the car at the top, the car's left on the left. View during Alignment faces
           the front plates from the front (the car's right is then on your left, so LF and
           RF swap places) and the rear plates from the rear, with each pair headed the way
           the app heads it. This is the IntelAlign app's GridPlateView.plateFlipped rule
           (right-hand wheels only), confirmed by CSM on 2026-09-24. */
        var leftCell = cornerCell(a[1], a[2], a[5], res.result, plate, false), rightCell = cornerCell(a[3], a[4], a[6], res.result, plate, true);
        var pair = front && carView ? rightCell + leftCell : leftCell + rightCell;
        rows.push('<div class="gp-axle">' + (carView ? '<p class="gp-axle__h">' + (front ? 'Front Looking Back' : 'Rear Looking Forward') + '</p>' : '') +
                  '<div class="gp-axle__pair">' + pair + '</div></div>');
        spoken.push((front ? 'Front' : 'Rear') + ' toe ' + withUnit(raw, st.toeUnit) + ' ' + ((front ? st.frontIn : st.rearIn) ? 'in' : 'out') +
                    ' reads ' + withUnit(res.result.laserPosition, plate.unit) + ' on the ' + (front ? 'rear' : 'front') + ' plates.');
      });

      var any = results.front || results.rear, calcHtml = '';
      if (any) {
        var bothLinear = linearPlate && linearToe;
        var line = function (k, v) { return '<div class="gp-calc__row"><span>' + k + '</span><strong>' + v + '</strong></div>'; };
        var present = [results.front, results.rear].filter(Boolean);
        var allDirect = present.every(function (r) { return r.isDirectReading; });
        /* The combined ratio only describes the answer when both sides are distances;
           an angle on either side drops its wheel diameter out of the math. */
        var how = bothLinear
          ? 'Car divided by plate wheelbase, times plate divided by car wheel diameter, per axle. 1.000× means the plate reads toe directly.'
          : !linearToe && !linearPlate ? 'Car divided by plate wheelbase. Your toe and the plate are both angles, so no wheel diameter is involved.'
          : linearToe ? 'Car divided by plate wheelbase. Your toe figure is a chord across your wheel, so it is turned into an angle over the wheel diameter you entered; the plate reads an angle, so its own wheel diameter does not matter.'
          : 'Car divided by plate wheelbase. Your toe is an angle; the plate’s markings are a chord across its reference wheel, so the plate wheel diameter turns that angle back into a distance.';
        calcHtml = '<' + subTag + ' class="gp__h4">Calculation</' + subTag + '>' + line('Wheelbase ratio', any.wheelbaseRatio.toFixed(3) + '×') +
          (bothLinear && results.front ? line('Front combined', results.front.ratio.toFixed(3) + '×') : '') +
          (bothLinear && results.rear ? line('Rear combined', results.rear.ratio.toFixed(3) + '×') : '') +
          '<p class="gp__note">' + how + '</p>' +
          (allDirect && any.isSameWheelbase
            ? '<p class="gp__note gp__note--good">This plate is graduated for this wheelbase and printed in your toe unit, so it reads your toe directly.</p>'
            : any.isSameWheelbase ? '<p class="gp__note gp__note--good">Same wheelbase as the plate, so the only correction left is for units or wheel diameter.</p>' : '');
      }
      show(rows.join(''), calcHtml, spoken.join(' '));
    }

    /* Keep the results in view under a sticky theme header, if the theme has one, and
       only pin the panel when the whole of it fits in the viewport; a pinned panel
       taller than the window would hide its own lower rows. */
    function stickyTop() {
      var hdr = document.querySelector('.header-section, header'), top = 16;
      if (hdr) { var pos = getComputedStyle(hdr).position; if (pos === 'sticky' || pos === 'fixed') top = hdr.getBoundingClientRect().height + 16; }
      rootEl.style.setProperty('--gp-sticky-top', top + 'px');
      if (panel) panel.classList.toggle('is-pinned', panel.offsetHeight + top + 16 <= window.innerHeight);
    }
    window.addEventListener('resize', stickyTop);

    var uni = q('[data-gp-universal]');
    if (uni) uni.addEventListener('click', function () {
      touched = true;
      f.plateWheelbase.value = UNIVERSAL.wheelbaseMM; setRadio('plate_unit', 'mm'); setRadio('plate_reads', UNIVERSAL.graduationUnit); render();
    });
    var onEdit = function () { touched = true; render(); };
    rootEl.addEventListener('input', onEdit);
    rootEl.addEventListener('change', onEdit);
    recall();
    render();
  }

  function initAll(scope) { (scope || document).querySelectorAll('[data-gp]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})(typeof window !== 'undefined' ? window : globalThis);
